"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSendGreetingIfPendingWithPolling = useSendGreetingIfPendingWithPolling;
// packages/shared-react/src/hooks/useSendGreetingIfPendingWithPolling.ts
const react_1 = require("react");
const shared_1 = require("@heartcheck/shared");
const useSendMessage_1 = require("./useSendMessage");
/** --------- Small utilities --------- */
const safeLS = {
    get(key) {
        try {
            return window.localStorage.getItem(key);
        }
        catch {
            return null;
        }
    },
    set(key, value) {
        try {
            window.localStorage.setItem(key, value);
        }
        catch {
            /* ignore */
        }
    },
    remove(key) {
        try {
            window.localStorage.removeItem(key);
        }
        catch {
            /* ignore */
        }
    },
};
const ts = (s) => {
    const n = s ? Date.parse(s) : NaN;
    return Number.isFinite(n) ? n : 0;
};
/** Build a fresh GraphQL query each time with a salt comment to defeat client/middleware caching */
function buildLoadEventsQuery(salt) {
    return new shared_1.GraphqlQuery(`
    # poll-salt: ${salt}
    query EventsByConversation($conversationId: ID!, $limit: Int) {
      eventsByConversationId(conversationId: $conversationId, limit: $limit) {
        id
        conversationId
        sender
        timestamp
        agentId
        userId
        event {
          message
          innerDialog
          actionRequested
          actionResult
        }
      }
    }
  `, {
        // ✅ use ID token so the /graphql private proxy authorizes correctly
        forwardAuthMode: "userpools",
    });
}
/** Minimal event validator */
function isValidEvent(e) {
    try {
        return (typeof e?.id === "string" &&
            typeof e?.timestamp === "string" &&
            typeof e?.conversationId === "string" &&
            typeof e?.sender === "string" &&
            typeof e?.agentId === "string" &&
            !!(e?.event?.message ||
                e?.event?.innerDialog ||
                e?.event?.actionRequested ||
                e?.event?.actionResult));
    }
    catch {
        return false;
    }
}
/** ========= Robust polling helpers (timeout + backoff + single-flight) ========= */
const MIN_INTERVAL_MS = 1200; // success cadence
const MAX_INTERVAL_MS = 15000; // backoff cap
const REQ_TIMEOUT_MS = 8000; // per request timeout
const BACKOFF_JITTER_MAX = 250; // add up to 250ms jitter
const TARGET_EVENTS = 2; // hello + welcome
const GREETING_TEXT = "hello";
const START_ACTION = "start";
function nextDelayMs(errorStreak) {
    const base = Math.min(MIN_INTERVAL_MS * Math.pow(2, Math.max(0, errorStreak)), MAX_INTERVAL_MS);
    const jitter = Math.floor(Math.random() * BACKOFF_JITTER_MAX);
    return base + jitter;
}
function pendingKeyFor(cid) {
    return `pending-greeting-${cid}`;
}
function sentKeyFor(cid) {
    return `greeting-sent-${cid}`;
}
/** Abortable timeout wrapper (soft timeout around a promise) */
function withTimeout(promise, ms) {
    let t;
    const timeout = new Promise((_, rej) => {
        t = window.setTimeout(() => rej(new DOMException("Fetch is aborted", "AbortError")), ms);
    });
    return Promise.race([promise, timeout]).finally(() => window.clearTimeout(t));
}
/** Compatibility shim for differing GraphqlQuery result shapes across code versions */
async function runEventsQuery(conversationId) {
    const q = buildLoadEventsQuery(String(Date.now())); // bust caches/dedupers
    const anyQuery = q;
    const fn = anyQuery.invoke ?? anyQuery.execute ?? anyQuery.run ?? anyQuery.fetch;
    if (typeof fn !== "function") {
        throw new Error("GraphqlQuery has no supported execute method (expected invoke/execute/run/fetch).");
    }
    // Ensure we have an ID token before firing (prevents immediate 401 loops)
    const idToken = await (0, shared_1.getIdTokenWithFallback)();
    if (!idToken) {
        throw new Error("No ID token available");
    }
    // We can't pass an AbortSignal into GraphqlQuery here, so we apply a soft timeout wrapper.
    const res = await withTimeout(fn.call(q, { conversationId, limit: 25 }), REQ_TIMEOUT_MS);
    const list = res?.data?.eventsByConversationId ??
        res?.eventsByConversationId ??
        [];
    return (Array.isArray(list) ? list : [])
        .filter(isValidEvent)
        .sort((a, b) => ts(a.timestamp) - ts(b.timestamp));
}
/** Safely read optional carryover agent id without depending on Conversation typing */
function getCarryoverAgentId(conversation) {
    const c = conversation;
    const maybe = c?.carryoverSystem?.agentId;
    return typeof maybe === "string" && maybe ? maybe : undefined;
}
/** Resolve the agent we should greet: prefer handoff/carryover, else conversation.agent */
function resolveAgentId(conversation) {
    const handoffAgentId = getCarryoverAgentId(conversation);
    const c = conversation;
    const defaultAgentId = typeof c?.agent === "string" && c.agent ? c.agent : undefined;
    return handoffAgentId ?? defaultAgentId;
}
function useSendGreetingIfPendingWithPolling({ chatId, conversation, isConversationLoaded, 
// agents,
// isAgentListLoaded,
extraTrigger = 0, }) {
    const sendMessage = (0, useSendMessage_1.useAgentApiSendMessage)();
    // Exposed state
    const [events, setEvents] = (0, react_1.useState)(undefined);
    const [loading, setLoading] = (0, react_1.useState)(true);
    // Engine refs
    const inFlightRef = (0, react_1.useRef)(false);
    const stoppedRef = (0, react_1.useRef)(false);
    const errorStreak = (0, react_1.useRef)(0);
    const timerRef = (0, react_1.useRef)(null);
    const greetedRef = (0, react_1.useRef)(false);
    // Stable "poll now" indirection to avoid circular hook deps
    const pollNowRef = (0, react_1.useRef)(() => { });
    const clearTimer = (0, react_1.useCallback)(() => {
        if (timerRef.current != null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);
    const stopPolling = (0, react_1.useCallback)(() => {
        stoppedRef.current = true;
        clearTimer();
    }, [clearTimer]);
    const scheduleNext = (0, react_1.useCallback)((delay) => {
        clearTimer();
        if (stoppedRef.current)
            return;
        timerRef.current = window.setTimeout(() => {
            pollNowRef.current?.();
        }, delay);
    }, [clearTimer]);
    const maybeSendGreeting = (0, react_1.useCallback)(async (_reason) => {
        if (!chatId || !isConversationLoaded)
            return;
        const sentKey = sentKeyFor(chatId);
        const pendKey = pendingKeyFor(chatId);
        // Already sent in this session or a previous one?
        if (greetedRef.current || safeLS.get(sentKey) === "1")
            return;
        const agentId = resolveAgentId(conversation);
        if (!agentId)
            return;
        // If a previous flow marked it pending, proceed; otherwise mark pending now.
        const hasPending = safeLS.get(pendKey) === "true";
        if (!hasPending)
            safeLS.set(pendKey, "true");
        try {
            greetedRef.current = true;
            safeLS.set(sentKey, "1");
            safeLS.remove(pendKey);
            // Deterministic idempotency key so server ignores duplicates
            await sendMessage({
                chatId,
                agentId,
                event: { message: GREETING_TEXT, actionRequested: START_ACTION },
                clientMessageId: `greet:${chatId}`,
            });
            // Nudge the poller a few times
            pollNowRef.current?.();
            setTimeout(() => pollNowRef.current?.(), 400);
            setTimeout(() => pollNowRef.current?.(), 900);
            setTimeout(() => pollNowRef.current?.(), 1500);
        }
        catch {
            // Roll back the "sent" markers so we can try again later
            greetedRef.current = false;
            safeLS.remove(sentKey);
            safeLS.set(pendKey, "true");
        }
    }, [chatId, isConversationLoaded, conversation, sendMessage]);
    const pollOnce = (0, react_1.useCallback)(async () => {
        if (!chatId || stoppedRef.current || inFlightRef.current)
            return;
        // Pause if hidden or offline to avoid background throttling/aborts
        if (typeof document !== "undefined" && document.hidden) {
            scheduleNext(MIN_INTERVAL_MS);
            return;
        }
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            scheduleNext(MIN_INTERVAL_MS);
            return;
        }
        inFlightRef.current = true;
        try {
            const raw = await runEventsQuery(chatId);
            setEvents(raw);
            if (raw.length >= 1 && loading) {
                setLoading(false);
            }
            // If still zero events, proactively send greeting
            if (raw.length === 0 && isConversationLoaded) {
                await maybeSendGreeting("no-events-yet");
            }
            // Gate satisfied: ≥2 events OR an agent message exists
            if (raw.length >= TARGET_EVENTS ||
                raw.some((e) => e.sender === "agent")) {
                // clear pending flag now that handshake completed
                safeLS.remove(pendingKeyFor(chatId));
                errorStreak.current = 0;
                stopPolling();
                return;
            }
            // Successful poll but not enough yet—gentle cadence
            errorStreak.current = 0;
            scheduleNext(MIN_INTERVAL_MS);
        }
        catch (err) {
            // Swallow abort/timeouts quietly; back off
            const msg = String(err?.message ?? "");
            const isAbort = err?.name === "AbortError" || /aborted|abort/i.test(msg);
            if (!isAbort) {
                // keep quiet to avoid console floods
            }
            errorStreak.current += 1;
            scheduleNext(nextDelayMs(errorStreak.current));
        }
        finally {
            inFlightRef.current = false;
        }
    }, [
        chatId,
        isConversationLoaded,
        loading,
        maybeSendGreeting,
        scheduleNext,
        stopPolling,
    ]);
    // Stable "poll now"
    const pollNow = (0, react_1.useCallback)(() => {
        if (!chatId)
            return;
        if (stoppedRef.current)
            return;
        if (inFlightRef.current)
            return;
        void pollOnce();
    }, [chatId, pollOnce]);
    // Keep the ref in sync with the current pollNow without creating a dep chain
    (0, react_1.useEffect)(() => {
        pollNowRef.current = pollNow;
    }, [pollNow]);
    // Lifecycle: reset/start when chat changes
    (0, react_1.useEffect)(() => {
        setEvents(undefined);
        setLoading(true);
        inFlightRef.current = false;
        stoppedRef.current = false;
        errorStreak.current = 0;
        greetedRef.current = false;
        clearTimer();
        if (chatId) {
            // Kick immediately, then schedule next based on results
            pollNow();
        }
        return () => {
            stopPolling();
        };
    }, [chatId, pollNow, clearTimer, stopPolling]);
    // If a previous app session marked this chat as "pending greeting", try once now.
    (0, react_1.useEffect)(() => {
        if (!chatId || !isConversationLoaded)
            return;
        const hasPending = safeLS.get(pendingKeyFor(chatId)) === "true";
        if (hasPending) {
            void maybeSendGreeting("pending-flag");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, isConversationLoaded]);
    // External nudges (e.g., responding flip true→false)
    (0, react_1.useEffect)(() => {
        if (!chatId)
            return;
        pollNow();
    }, [chatId, extraTrigger, pollNow]);
    // Visibility/online listeners: quick resume when user returns
    (0, react_1.useEffect)(() => {
        const onVisibility = () => {
            if (!document.hidden) {
                errorStreak.current = 0;
                scheduleNext(200);
            }
        };
        const onOnline = () => {
            errorStreak.current = 0;
            scheduleNext(200);
        };
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("online", onOnline);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("online", onOnline);
        };
    }, [scheduleNext]);
    return {
        events,
        loading: loading && ((events?.length ?? 0) === 0),
        pollNow,
    };
}
